/**
 * <ec-footer-accordion>
 *
 * Wraps the footer link columns. Below the mobile breakpoint, each column
 * heading becomes a toggle that collapses the matching list. Above it, the
 * grid layout is restored and lists are always visible. No focus trap or
 * scroll lock — these are inline disclosures, not modals.
 */

// Must mirror the mobile breakpoint used in assets/ec-footer.css.
const MOBILE_QUERY = '(max-width: 639.98px)';

class EcFooterAccordion extends HTMLElement {
  #mediaQuery = null;
  #onMediaChange = null;
  #onToggleClick = null;
  #toggles = [];

  connectedCallback() {
    this.#toggles = Array.from(this.querySelectorAll('[data-ec-footer-toggle]'));
    if (this.#toggles.length === 0) return;

    this.#onToggleClick = (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLElement)) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      this.#setExpanded(button, !expanded);
    };

    for (const button of this.#toggles) {
      button.addEventListener('click', this.#onToggleClick);
    }

    this.#mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.#onMediaChange = (event) => this.#applyMode(event.matches);
    this.#mediaQuery.addEventListener('change', this.#onMediaChange);
    this.#applyMode(this.#mediaQuery.matches);
  }

  disconnectedCallback() {
    for (const button of this.#toggles) {
      button.removeEventListener('click', this.#onToggleClick);
    }
    if (this.#mediaQuery && this.#onMediaChange) {
      this.#mediaQuery.removeEventListener('change', this.#onMediaChange);
    }
    this.#toggles = [];
    this.#mediaQuery = null;
    this.#onMediaChange = null;
    this.#onToggleClick = null;
  }

  #applyMode(isMobile) {
    for (const button of this.#toggles) {
      const panel = this.#getPanel(button);
      if (!panel) continue;
      if (isMobile) {
        // Collapsed by default on mobile.
        button.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      } else {
        // Always open on desktop; expose state for SR consistency.
        button.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
      }
    }
  }

  #setExpanded(button, expanded) {
    const panel = this.#getPanel(button);
    if (!panel) return;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.hidden = !expanded;
  }

  #getPanel(button) {
    const id = button.getAttribute('aria-controls');
    if (!id) return null;
    return this.ownerDocument.getElementById(id);
  }
}

if (!customElements.get('ec-footer-accordion')) {
  customElements.define('ec-footer-accordion', EcFooterAccordion);
}
